import {
  Box,
  Button,
  Collapse,
  Flex,
  Image,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useContext, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';
import googleIcon from '../../../assets/images/google.svg';
import microsoftIcon from '../../../assets/images/microsoft.svg';
import oktaIcon from '../../../assets/images/okta.svg';
import { HttpContext } from '../../../context/HttpContext';
import sentry from '../../../sentry';

const SSO_OPTIONS = [
  {
    label: 'Google',
    value: 'google',
    icon: googleIcon,
  },
  {
    label: 'Microsoft',
    value: 'microsoft',
    icon: microsoftIcon,
  },
  {
    label: 'Okta',
    value: 'okta',
    icon: oktaIcon,
  },
];

const emailSchema = yup
  .string()
  .email('Please enter a valid email address')
  .required('Email is required');

const NOTICES = {
  password: {
    color: 'secondaryDark',
    text: "This email isn't set up for single sign-on — use your password to sign in.",
  },
  error: {
    color: 'red.500',
    text: "Couldn't check single sign-on for this email. Please try again.",
  },
};

const handleSSOSignIn = (provider, setOauthPopup) => {
  const baseUrl =
    process.env.REACT_APP_API_URL_AXIOS || window.env?.REACT_APP_API_URL_AXIOS;
  const authUrl = `${baseUrl}/accounts/auth/${provider}`;
  const popup = window.open(authUrl, '_blank');

  if (popup) {
    setOauthPopup(popup);
  } else {
    console.error(
      'Unable to open authentication window. Please allow popups for this site.'
    );
  }
};

function SsoOptions({ setOauthPopup }) {
  const { publicAxios } = useContext(HttpContext) || {};
  const [oktaStepOpen, setOktaStepOpen] = useState(false);
  const [oktaEmail, setOktaEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [notice, setNotice] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const activate = (value) => {
    if (isDiscovering) return;

    if (value !== 'okta') {
      handleSSOSignIn(value, setOauthPopup);
      return;
    }

    const willOpen = !oktaStepOpen;
    setOktaStepOpen(willOpen);
    if (!willOpen) {
      setNotice(null);
      setEmailError('');
    }
  };

  const handleKeyDown = (event, value) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      activate(value);
    }
  };

  const discoverOkta = async () => {
    if (isDiscovering) return;
    setNotice(null);

    try {
      emailSchema.validateSync(oktaEmail);
      setEmailError('');
    } catch (validationError) {
      setEmailError(validationError.message);
      return;
    }

    // Open the popup synchronously inside the click gesture so browsers don't
    // block it — the discovery request that follows is async, and a popup
    // opened after an await is treated as non-user-initiated.
    const popup = window.open('', '_blank');
    if (!popup) {
      setNotice(NOTICES.error);
      return;
    }

    setIsDiscovering(true);
    try {
      const { data } = await publicAxios.get('/internal/v1/sso/discover', {
        params: { email: oktaEmail },
        timeout: 10000,
      });

      if (!mountedRef.current) {
        popup.close();
        return;
      }
      if (popup.closed) {
        setNotice(NOTICES.error);
        return;
      }

      if (data?.provider === 'okta' && data?.url) {
        popup.location.href = data.url;
        setOauthPopup(popup);
      } else {
        popup.close();
        setNotice(NOTICES.password);
      }
    } catch (discoveryError) {
      popup.close();
      const apiMessage = discoveryError.response?.data?.errors?.[0];
      if (!mountedRef.current) return;
      if (apiMessage) {
        setNotice({ color: 'red.500', text: apiMessage });
      } else {
        sentry.sendError(discoveryError);
        setNotice(NOTICES.error);
      }
    } finally {
      if (mountedRef.current) setIsDiscovering(false);
    }
  };

  return (
    <VStack w="full" spacing={3}>
      <Flex w="full" justifyContent="center" gap={2}>
        {SSO_OPTIONS.map((option) => {
          const isExpandable = option.value === 'okta';
          const isExpanded = isExpandable && oktaStepOpen;
          return (
            <Flex
              key={option.value}
              flex="1"
              py="10px"
              bg="white"
              borderRadius="10px"
              border={isExpanded ? '1px solid #FF9554' : '1px solid #D0D5DD'}
              justifyContent="center"
              alignItems="center"
              gap={2}
              cursor={isDiscovering ? 'not-allowed' : 'pointer'}
              opacity={isDiscovering ? 0.6 : 1}
              tabIndex={0}
              role="button"
              aria-label={`Sign in with ${option.label}`}
              aria-expanded={isExpandable ? oktaStepOpen : undefined}
              aria-disabled={isDiscovering || undefined}
              _hover={{
                border: '1px solid #FF9554',
              }}
              onClick={() => activate(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
            >
              <Image
                src={option.icon}
                boxSize="20px"
                alt={`${option.label} logo`}
              />
              <Text textStyle="bodySmall" color="secondaryDark">
                Sign in with {option.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      <Collapse in={oktaStepOpen} style={{ width: '100%' }} unmountOnExit>
        <Box w="full">
          <Input
            variant="filled"
            type="email"
            placeholder="Enter your work email"
            aria-label="Work email"
            value={oktaEmail}
            onChange={(e) => setOktaEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              discoverOkta();
            }}
            isInvalid={!!emailError}
            mb={2}
          />
          {emailError && (
            <Text pl={4} color="red.500" fontSize="sm">
              {emailError}
            </Text>
          )}
          <Button
            type="button"
            w="full"
            mt={2}
            isLoading={isDiscovering}
            isDisabled={isDiscovering}
            onClick={discoverOkta}
          >
            Continue
          </Button>
          {notice && (
            <Text mt={3} fontSize="sm" color={notice.color}>
              {notice.text}
            </Text>
          )}
        </Box>
      </Collapse>
    </VStack>
  );
}

export default SsoOptions;
